/**
 * MoveWithUnsavedChangesDialog Component
 *
 * Confirmation dialog shown when user tries to move a playbook with unsaved changes.
 * Provides three options: Cancel, Discard & Move, or Save & Move.
 */

import React from 'react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@enfomentis/ui';

export interface MoveWithUnsavedChangesDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when user clicks Cancel (stay on page, abort move) */
  onClose: () => void;
  /** Called when user clicks "Discard & Move" */
  onDiscardAndMove: () => void;
  /** Called when user clicks "Save & Move" */
  onSaveAndMove: () => void;
  /** Whether save operation is in progress */
  isSaving?: boolean;
}

export const MoveWithUnsavedChangesDialog: React.FC<MoveWithUnsavedChangesDialogProps> = ({
  isOpen,
  onClose,
  onDiscardAndMove,
  onSaveAndMove,
  isSaving = false,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalContent>
        <ModalHeader>Unsaved Changes</ModalHeader>
        <ModalBody>
          <p className="text-text-secondary">
            This playbook has unsaved changes. What would you like to do?
          </p>
        </ModalBody>
        <ModalFooter className="flex gap-2">
          <Button variant="flat" onPress={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            color="danger"
            variant="flat"
            onPress={onDiscardAndMove}
            isDisabled={isSaving}
          >
            Discard & Move
          </Button>
          <Button
            color="primary"
            onPress={onSaveAndMove}
            isLoading={isSaving}
          >
            Save & Move
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
